import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Check } from 'lucide-react';
import { getInstagramPosts, getTikTokPosts, getYouTubePosts } from '../services/api';

export default function PostSelector({ isOpen, onClose, username, channelUrl, quantity, serviceType, platform, onConfirm }) {
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState(new Map()); // Map<postId, quantity>
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [showConfirmButtons, setShowConfirmButtons] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalData, setLimitModalData] = useState({ maxPosts: 0, quantity: 0, serviceType: '' });
  const scrollContainerRef = useRef(null);
  const currentLimit = useRef(platform === 'instagram' ? (serviceType === 'views' ? 12 : 15) : 9);
  const hasLoadedRef = useRef(false);
  const prevCountRef = useRef(0); // para detetar se "carregar mais" trouxe resultados novos

  // Mínimo por post baseado no tipo de serviço
  const getMinPerPost = () => {
    return serviceType === 'views' ? 500 : 50;
  };

  const MIN_PER_POST = getMinPerPost();

  useEffect(() => {
    let cancelled = false;

    console.log('🎬 PostSelector useEffect:', { isOpen, username, hasLoaded: hasLoadedRef.current });

    if (isOpen && username && !hasLoadedRef.current) {
      console.log('🚀 Iniciando carregamento de posts...');
      hasLoadedRef.current = true;
      loadPosts();
    } else if (isOpen && !username) {
      console.error('❌ Modal aberto mas username não foi fornecido!');
      setError('Username não fornecido');
    }

    // Reset quando o modal fechar
    if (!isOpen) {
      console.log('🔄 Modal fechado - resetando estado');
      hasLoadedRef.current = false;
      setError(null);
      setPosts([]);
      setSelectedPosts(new Map());
      setShowConfirmButtons(false);
      setLoadTimedOut(false);
      currentLimit.current = platform === 'instagram' ? (serviceType === 'views' ? 12 : 15) : 9;
      prevCountRef.current = 0;
      setHasMore(true);
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, username]);

  const loadPosts = async (isLoadingMore = false) => {
    setLoadTimedOut(false);
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setLoadingMore(false);
      setLoadTimedOut(true);
    }, 30000);

    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      console.log('🔄 Carregando posts para:', username, 'plataforma:', platform);
      console.log('📊 Limite atual:', currentLimit.current);

      const limit = currentLimit.current;

      // Usar a função correta baseado na plataforma
      let result;
      if (platform === 'tiktok') {
        result = await getTikTokPosts(username, limit);
      } else if (platform === 'youtube') {
        // Para YouTube, usar a URL do canal fornecida ou construir se necessário
        const rawHandle = username.startsWith('http')
          ? (username.match(/youtube\.com\/@?([\w.%-]+)/)?.[1] || username)
          : username.replace('@', '');
        const youtubeUrl = channelUrl || `https://youtube.com/@${rawHandle.toLowerCase()}`;

        console.log('📺 URL do YouTube a ser usada:', youtubeUrl);
        result = await getYouTubePosts(youtubeUrl, limit);
      } else {
        // Para Instagram: usar actor de reels se for serviço de views
        const onlyReels = serviceType === 'views';
        result = await getInstagramPosts(username, limit, onlyReels);
      }

      console.log('✅ Resultado recebido:', result);

      if (!result || !result.data) {
        throw new Error('Resposta inválida da API');
      }

      // Filtrar posts baseado no tipo de serviço
      // Para Instagram com views, não precisa filtrar (o actor de reels já retorna só vídeos)
      let filteredPosts = result.data;
      if (serviceType === 'views' && platform !== 'instagram') {
        // Para TikTok e YouTube não precisa filtrar (já são todos vídeos)
        // Mas deixar o filtro como backup se necessário
        filteredPosts = result.data.filter(post => {
          const isVideoByType =
            post.type === 'Video' ||
            post.type === 'Reel' ||
            post.type === 'video' ||
            post.type === 'reel' ||
            post.media_type === 'Video' ||
            post.media_type === 'Reel';

          // Ou tem duração de vídeo (indicador claro de que é vídeo)
          const hasVideoDuration = post.video_duration > 0 || post.videoDuration > 0;

          return isVideoByType || hasVideoDuration;
        });
        console.log(`🎬 Filtrados ${filteredPosts.length} vídeos de ${result.data.length} posts`);
      }

      setPosts(filteredPosts);
      setError(null);

      // Só confirmar "sem mais" se "carregar mais" não trouxe resultados novos
      if (result.data.length === 0) {
        setHasMore(false);
      } else if (isLoadingMore && result.data.length <= prevCountRef.current) {
        setHasMore(false);
      }
      prevCountRef.current = result.data.length;

      console.log('✅ Posts carregados com sucesso:', result.data.length);
    } catch (err) {
      console.error('❌ Erro ao carregar posts:', err);
      console.error('❌ Mensagem:', err.message);
      console.error('❌ Stack:', err.stack);

      const errorMessage = err.message || 'Erro ao carregar posts. Tente novamente.';
      setError(errorMessage);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const increment = platform === 'instagram' ? 15 : 9; // 15 para Instagram, 9 para outros
    currentLimit.current += increment;
    loadPosts(true);
  };

  const togglePostSelection = (post) => {
    const newSelected = new Map(selectedPosts);

    if (newSelected.has(post.id)) {
      // Remover post da seleção
      newSelected.delete(post.id);
    } else {
      // Verificar se adicionar este post vai deixar abaixo do mínimo
      const futureSize = newSelected.size + 1;
      const quantityPerPost = Math.floor(quantity / futureSize);

      if (quantityPerPost < MIN_PER_POST) {
        const maxPosts = Math.floor(quantity / MIN_PER_POST);
        setLimitModalData({ maxPosts, quantity, serviceType });
        setShowLimitModal(true);
        return;
      }

      // Adicionar post à seleção
      newSelected.set(post.id, 0); // A quantidade será calculada depois
    }

    setSelectedPosts(newSelected);
  };

  const getDistributedQuantities = () => {
    if (selectedPosts.size === 0) return new Map();

    const quantityPerPost = Math.floor(quantity / selectedPosts.size);
    const remainder = quantity % selectedPosts.size;

    const distributed = new Map();
    let index = 0;

    selectedPosts.forEach((_, postId) => {
      const qty = quantityPerPost + (index < remainder ? 1 : 0);
      distributed.set(postId, qty);
      index++;
    });

    return distributed;
  };

  const handleConfirm = () => {
    // Determinar o nome do tipo de post baseado na plataforma e serviço
    const postTypeSingular = (platform === 'tiktok' || platform === 'youtube') ? 'vídeo' : serviceType === 'views' ? 'Reel' : 'post';
    const postTypePlural = (platform === 'tiktok' || platform === 'youtube') ? 'vídeos' : serviceType === 'views' ? 'Reels' : 'posts';

    if (selectedPosts.size === 0) {
      alert(`Por favor, selecione pelo menos um ${postTypeSingular}.`);
      return;
    }

    const distributedQuantities = getDistributedQuantities();

    // Verificar se algum post tem menos de MIN_PER_POST
    const minQuantity = Math.min(...Array.from(distributedQuantities.values()));

    if (minQuantity < MIN_PER_POST) {
      const postsNeeded = Math.ceil(quantity / MIN_PER_POST);
      alert(`⚠️ Cada ${postTypeSingular} precisa de pelo menos ${MIN_PER_POST} ${serviceType}.\n\nPara ${quantity.toLocaleString()} ${serviceType}, selecione no máximo ${postsNeeded} ${postTypePlural}.`);
      return;
    }

    // Mostrar botões de confirmação
    setShowConfirmButtons(true);
  };

  const handleAddToCart = () => {
    const distributedQuantities = getDistributedQuantities();

    // Converter Map para array de objetos
    const selectedData = Array.from(distributedQuantities.entries()).map(([postId, qty]) => {
      const post = posts.find(p => p.id === postId);
      return {
        postId,
        postUrl: post.url,
        quantity: qty,
        thumbnailUrl: post.thumbnailUrl
      };
    });

    onConfirm(selectedData, 'cart');
  };

  const handleCheckout = () => {
    const distributedQuantities = getDistributedQuantities();

    // Converter Map para array de objetos
    const selectedData = Array.from(distributedQuantities.entries()).map(([postId, qty]) => {
      const post = posts.find(p => p.id === postId);
      return {
        postId,
        postUrl: post.url,
        quantity: qty,
        thumbnailUrl: post.thumbnailUrl
      };
    });

    onConfirm(selectedData, 'checkout');
  };

  if (!isOpen) return null;

  // Filtrar posts baseado no tipo de serviço
  // Para TikTok e YouTube, todos são vídeos. Para Instagram, filtrar por Reel/Video se for views
  const filteredPosts = serviceType === 'views'
    ? posts.filter(post => post.type === 'Reel' || post.type === 'Video')
    : posts;

  const distributedQuantities = getDistributedQuantities();
  const quantityPerPost = selectedPosts.size > 0 ? Math.floor(quantity / selectedPosts.size) : 0;
  const serviceName = serviceType === 'likes' ? 'Likes' : 'Visualizações';
  const maxPostsAllowed = Math.floor(quantity / MIN_PER_POST);
  const isOverLimit = selectedPosts.size > maxPostsAllowed;

  // Determinar o nome do tipo de post baseado na plataforma e serviço
  const getPostTypeName = (singular = false) => {
    if (platform === 'tiktok' || platform === 'youtube') {
      return singular ? 'vídeo' : 'vídeos';
    }
    if (serviceType === 'views') {
      return singular ? 'Reel' : 'Reels';
    }
    return singular ? 'post' : 'posts';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] shadow-2xl relative flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>

          <h3 className="text-2xl font-extrabold text-gray-900 mb-2">
            Selecione {getPostTypeName()} para {serviceName}
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Escolha {(platform === 'tiktok' || platform === 'youtube') ? 'os vídeos' : serviceType === 'views' ? 'os Reels' : 'as publicações'} de <span className="font-bold">@{username}</span> para distribuir <span className="font-bold">{quantity.toLocaleString()} {serviceName.toLowerCase()}</span>
          </p>

          {selectedPosts.size > 0 ? (
            <div className={`rounded-lg sm:rounded-xl p-2 sm:p-4 border-2 ${
              isOverLimit
                ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-300'
                : 'bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200'
            }`}>
              <div className="flex items-center justify-center gap-2 sm:flex-col sm:gap-0">
                <div className="text-xs sm:text-sm text-gray-700 sm:mb-2">
                  <span className={`font-bold ${isOverLimit ? 'text-red-600' : 'text-pink-600'}`}>
                    {selectedPosts.size}
                  </span> {getPostTypeName(selectedPosts.size === 1)} {selectedPosts.size === 1 ? 'selecionado' : 'selecionados'}
                  {maxPostsAllowed > 0 && (
                    <span className="text-gray-500"> (máx: {maxPostsAllowed})</span>
                  )}
                </div>
                <div className={`text-base sm:text-2xl font-extrabold ${isOverLimit ? 'text-red-600' : 'text-purple-600'}`}>
                  {quantityPerPost.toLocaleString()} {serviceName.toLowerCase()} por post
                </div>
                <div className="hidden sm:block text-xs text-gray-600 mt-1">
                  {isOverLimit ? (
                    <span className="text-red-600 font-semibold">
                      ⚠️ Mínimo de {MIN_PER_POST} {serviceName.toLowerCase()} por post
                    </span>
                  ) : (
                    'A quantidade será distribuída igualmente'
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
              <div className="text-lg sm:text-2xl font-bold text-purple-600">{quantity.toLocaleString()} {serviceName.toLowerCase()}</div>
              <div className="hidden sm:block text-xs text-gray-400 mt-1">
                (mín. {MIN_PER_POST} por {getPostTypeName(true)})
              </div>
            </div>
          )}
        </div>

        {/* Posts Grid */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-3 sm:p-6"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-4" />
              <p className="text-gray-600">Carregando publicações...</p>
              <p className="text-gray-400 text-sm font-medium mt-2 animate-pulse">Isto pode demorar até 30 segundos...</p>
            </div>
          ) : loadTimedOut ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-500 text-sm mb-3">A pesquisa demorou demasiado. Tente novamente.</p>
              <button
                onClick={() => loadPosts()}
                className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-6 py-2 rounded-xl font-bold text-sm hover:from-pink-700 hover:to-purple-700 transition"
              >
                Tentar novamente
              </button>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => loadPosts()}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
              >
                Tentar Novamente
              </button>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">
                {(platform === 'tiktok' || platform === 'youtube')
                  ? 'Nenhum vídeo encontrado neste perfil.'
                  : serviceType === 'views'
                    ? 'Nenhum Reel encontrado neste perfil.'
                    : 'Nenhum post encontrado.'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {filteredPosts.map((post) => {
                  const isSelected = selectedPosts.has(post.id);
                  const allocatedQty = distributedQuantities.get(post.id) || 0;

                  return (
                    <div
                      key={post.id}
                      className={`relative rounded-lg sm:rounded-xl overflow-hidden border-2 sm:border-4 transition-all cursor-pointer ${
                        isSelected
                          ? 'border-pink-500 shadow-lg sm:shadow-xl'
                          : 'border-gray-200 hover:border-pink-300'
                      }`}
                      onClick={() => togglePostSelection(post)}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={post.displayUrl}
                          alt="Post"
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.error('❌ Erro ao carregar imagem do post:', post.displayUrl);
                            // Tentar usar thumbnail como fallback
                            if (e.target.src !== post.thumbnailUrl && post.thumbnailUrl) {
                              e.target.src = post.thumbnailUrl;
                            } else {
                              // Usar placeholder cinza
                              e.target.style.display = 'none';
                              e.target.parentElement.style.backgroundColor = '#e5e7eb';
                            }
                          }}
                        />

                        {/* Quantidade alocada — topo da imagem */}
                        {isSelected && allocatedQty > 0 && (
                          <div className="absolute top-2 left-2 right-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg px-2 py-1 shadow-lg">
                            <div className="text-white font-bold text-xs text-center">
                              +{allocatedQty.toLocaleString()} {serviceName.toLowerCase()}
                            </div>
                          </div>
                        )}

                        {/* Checkmark — canto superior direito */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-pink-500 rounded-full p-1.5 shadow-lg">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}

                        {/* Contador de likes/views — fundo da imagem */}
                        {(serviceType === 'views'
                          ? (post.videoViewCount > 0 || post.viewCount > 0)
                          : (platform !== 'youtube' && post.likesCount !== null && post.likesCount !== undefined)
                        ) && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                            <div className="text-white text-xs flex items-center gap-1">
                              {serviceType === 'views' ? (
                                <>👁️ {(post.videoViewCount || post.viewCount || 0).toLocaleString()}</>
                              ) : (
                                <>❤️ {post.likesCount.toLocaleString()}</>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Botão Carregar Mais */}
              {hasMore && !loadingMore && (
                <div className="flex justify-center py-6">
                  <button
                    onClick={handleLoadMore}
                    className="flex items-center gap-2 bg-purple-100 text-purple-700 px-6 py-3 rounded-xl font-bold hover:bg-purple-200 transition shadow-md hover:shadow-lg"
                  >
                    <span>Carregar Mais Posts</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}

              {loadingMore && (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              )}

              {!hasMore && filteredPosts.length > 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  ✓ {(platform === 'tiktok' || platform === 'youtube')
                    ? 'Todos os vídeos foram carregados'
                    : serviceType === 'views'
                      ? 'Todos os Reels foram carregados'
                      : 'Todos os posts foram carregados'
                  }
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-6 border-t border-gray-200 flex gap-2 sm:gap-3 flex-shrink-0">
          {!showConfirmButtons ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base hover:bg-gray-300 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedPosts.size === 0}
                className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base hover:from-pink-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar ({selectedPosts.size} {getPostTypeName(selectedPosts.size === 1)})
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleAddToCart}
                className="flex-1 bg-white border-2 border-pink-600 text-pink-600 py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base hover:bg-pink-50 transition"
              >
                🛍️ Continuar a comprar
              </button>
              <button
                onClick={handleCheckout}
                className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-2.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base hover:from-pink-700 hover:to-purple-700 transition"
              >
                ✅ Finalizar Compra
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de Limite de Posts */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl max-w-md w-full mx-4 shadow-2xl animate-bounce-in">
            <div className="p-6">
              {/* Ícone de Aviso */}
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-br from-orange-100 to-red-100 rounded-full p-4">
                  <svg className="w-12 h-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              {/* Título */}
              <h3 className="text-xl font-extrabold text-gray-900 text-center mb-3">
                Não é possível adicionar mais {(platform === 'tiktok' || platform === 'youtube') ? 'vídeos' : serviceType === 'views' ? 'Reels' : 'posts'}!
              </h3>

              {/* Mensagem */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 mb-4 border-2 border-orange-200">
                <p className="text-gray-700 text-center mb-3">
                  Cada {(platform === 'tiktok' || platform === 'youtube') ? 'vídeo' : serviceType === 'views' ? 'Reel' : 'post'} precisa de pelo menos <span className="font-bold text-orange-600">{MIN_PER_POST} {limitModalData.serviceType}</span>.
                </p>
                <p className="text-gray-600 text-center text-sm">
                  Para <span className="font-bold text-purple-600">{limitModalData.quantity.toLocaleString()} {limitModalData.serviceType}</span>, pode selecionar no máximo{' '}
                  <span className="font-bold text-orange-600">
                    {limitModalData.maxPosts} {(platform === 'tiktok' || platform === 'youtube')
                      ? (limitModalData.maxPosts === 1 ? 'vídeo' : 'vídeos')
                      : limitModalData.maxPosts === 1
                        ? (serviceType === 'views' ? 'Reel' : 'post')
                        : (serviceType === 'views' ? 'Reels' : 'posts')
                    }
                  </span>.
                </p>
              </div>

              {/* Botão OK */}
              <button
                onClick={() => setShowLimitModal(false)}
                className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 rounded-xl font-bold hover:from-orange-700 hover:to-red-700 transition shadow-lg"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
